/**
 * Iter 190 — Content Strategy editing / versioning helpers.
 *
 * All manual-edit dialogs + revision history + carry-over modal live here
 * so the main BrandContentStrategyTab stays under ~700 lines.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, PenSquare, History, Sparkles, User as UserIcon, GitMerge,
  Bot, ArrowRight, ArrowLeftRight, Trash2, Plus, Shield,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

/* ---------- section labels ---------- */
export const SECTION_LABELS = {
  content_pillars: "Content pillars",
  themes: "Themes",
  proposed_topics: "Proposed topics",
  cadence: "Cadence",
  hashtag_bank: "Hashtag bank",
  content_mix: "Content mix",
  key_dates_to_watch: "Dates to watch",
  recommendations: "Strategic recommendations",
  north_star_metric: "North star metric",
  summary: "Executive summary",
};
export const TRACKED_SECTIONS = Object.keys(SECTION_LABELS);

/* ---------- Source badge ---------- */
export function SectionSourceBadge({ meta, small = false }) {
  if (!meta) return null;
  const source = meta.source || "ai";
  const label = source === "ai" ? "AI" : source === "carry_over" ? "Carried" : "Manual";
  const Icon = source === "ai" ? Bot : source === "carry_over" ? ArrowLeftRight : PenSquare;
  const tone = source === "ai"
    ? "border-sky-500 text-sky-800 bg-sky-50"
    : source === "carry_over"
      ? "border-violet-500 text-violet-800 bg-violet-50"
      : "border-amber-500 text-amber-900 bg-amber-50";
  const title = `${label}${meta.last_editor_name ? ` · ${meta.last_editor_name}` : ""}` +
                `${meta.last_edited_at ? ` · ${fmtRelative(meta.last_edited_at)}` : ""}` +
                `${meta.carried_from_version ? ` · from v${meta.carried_from_version}` : ""}`;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 border px-1 py-0.5 font-bold uppercase tracking-widest ${tone} ${small ? "text-[8px]" : "text-[9px]"}`}
      data-testid={`section-source-${source}`}
    >
      <Icon className={small ? "w-2.5 h-2.5" : "w-3 h-3"} />
      {label}
    </span>
  );
}

/* ---------- Field primitives (mirrors BucketTab FieldText/FieldTextarea/FieldChipList) ---------- */
function FieldText({ label, value, onChange, placeholder, hint, ...rest }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Input className="h-8 rounded-none text-xs" value={value ?? ""}
               placeholder={placeholder}
               onChange={(e) => onChange(e.target.value)}
               {...rest} />
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}
function FieldNum({ label, value, onChange, min, max, placeholder, ...rest }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Input type="number" min={min} max={max}
               className="h-8 rounded-none text-xs" value={value ?? ""}
               placeholder={placeholder}
               onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
               {...rest} />
    </div>
  );
}
function FieldTextarea({ label, value, onChange, rows = 3, placeholder, ...rest }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <Textarea rows={rows} className="rounded-none text-xs" value={value ?? ""}
                   placeholder={placeholder}
                   onChange={(e) => onChange(e.target.value)}
                   {...rest} />
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, ...rest }) {
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-block">{label}</Label>
      <select className="h-8 rounded-none border border-border bg-background px-2 text-xs w-full"
                 value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                 {...rest}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ---------- Section-specific friendly editors ---------- */

function PillarsEditor({ value, onChange }) {
  const pillars = value || [];
  const upd = (i, k, v) => {
    const next = [...pillars]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };
  const remove = (i) => onChange(pillars.filter((_, j) => j !== i));
  const add = () => onChange([...pillars, { name: "", description: "", share_pct: 0 }]);
  const totalShare = pillars.reduce((a, p) => a + (Number(p.share_pct) || 0), 0);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold">
          Pillars ({pillars.length}) · share {totalShare}%
        </div>
        {totalShare !== 100 && pillars.length > 0 && (
          <span className="text-[10px] text-amber-700 font-semibold">⚠︎ Shares should sum to 100</span>
        )}
      </div>
      {pillars.map((p, i) => (
        <div key={i} className="border border-border bg-white p-2 space-y-1"
                 data-testid={`fe-pillar-${i}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-1">
              <FieldText label="Name" value={p.name} onChange={(v) => upd(i, "name", v)}
                            placeholder="e.g. Origin & Craftsmanship" />
              <FieldTextarea label="Description" value={p.description}
                                    onChange={(v) => upd(i, "description", v)} rows={2}
                                    placeholder="1–2 sentences on what this pillar covers" />
            </div>
            <div className="w-20 shrink-0 pt-4">
              <FieldNum label="Share %" min={0} max={100}
                              value={p.share_pct}
                              onChange={(v) => upd(i, "share_pct", v)} />
            </div>
            <button type="button" onClick={() => remove(i)}
                        className="text-rose-600 hover:text-rose-700 pt-4"
                        data-testid={`fe-pillar-del-${i}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                onClick={add} data-testid="fe-pillar-add">
        <Plus className="w-3 h-3 mr-1" /> Add pillar
      </Button>
    </div>
  );
}

function TopicsEditor({ value, onChange }) {
  const topics = value || [];
  const upd = (i, k, v) => {
    const next = [...topics]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };
  const updFormats = (i, csv) => {
    const arr = csv.split(",").map(s => s.trim()).filter(Boolean);
    upd(i, "formats", arr);
  };
  const remove = (i) => onChange(topics.filter((_, j) => j !== i));
  const add = () => onChange([...topics, {
    title: "", pillar: "", formats: [], funnel_stage: "TOFU",
    target_persona: "", hook: "", cta: "",
  }]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold">
          Topics ({topics.length})
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                  onClick={add} data-testid="fe-topic-add">
          <Plus className="w-3 h-3 mr-1" /> Add topic
        </Button>
      </div>
      <div className="max-h-[420px] overflow-y-auto space-y-1.5 pr-1">
        {topics.map((t, i) => (
          <div key={i} className="border border-border bg-white p-2 space-y-1"
                    data-testid={`fe-topic-${i}`}>
            <div className="flex items-start gap-1.5">
              <div className="flex-1 space-y-1">
                <FieldText label={`#${i + 1} · Title`} value={t.title}
                              onChange={(v) => upd(i, "title", v)}
                              placeholder="Engaging, specific title" />
                <div className="grid grid-cols-3 gap-1.5">
                  <FieldText label="Pillar" value={t.pillar}
                                onChange={(v) => upd(i, "pillar", v)} />
                  <FieldSelect label="Stage" value={t.funnel_stage || "TOFU"}
                                       onChange={(v) => upd(i, "funnel_stage", v)}
                                       options={[
                                         { value: "TOFU", label: "TOFU" },
                                         { value: "MOFU", label: "MOFU" },
                                         { value: "BOFU", label: "BOFU" },
                                       ]} />
                  <FieldText label="Persona" value={t.target_persona}
                                onChange={(v) => upd(i, "target_persona", v)} />
                </div>
                <FieldText label="Formats (comma-sep)"
                              value={(t.formats || []).join(", ")}
                              onChange={(v) => updFormats(i, v)}
                              placeholder="blog_post, linkedin_post, instagram_carousel" />
                <FieldText label="Hook" value={t.hook}
                              onChange={(v) => upd(i, "hook", v)}
                              placeholder="Why this will resonate" />
                <FieldText label="CTA" value={t.cta}
                              onChange={(v) => upd(i, "cta", v)}
                              placeholder="What action to drive" />
              </div>
              <button type="button" onClick={() => remove(i)}
                          className="text-rose-600 hover:text-rose-700 pt-4"
                          data-testid={`fe-topic-del-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        {topics.length === 0 && (
          <div className="text-xs text-muted-foreground italic text-center p-4">
            No topics yet — add one to seed the strategy.
          </div>
        )}
      </div>
    </div>
  );
}

function ThemesEditor({ value, onChange }) {
  const themes = value || [];
  const upd = (i, k, v) => {
    const next = [...themes]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };
  const remove = (i) => onChange(themes.filter((_, j) => j !== i));
  const add = () => onChange([...themes, { name: "", pillar: "", angle: "" }]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold">
          Themes ({themes.length})
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                  onClick={add} data-testid="fe-theme-add">
          <Plus className="w-3 h-3 mr-1" /> Add theme
        </Button>
      </div>
      {themes.map((t, i) => (
        <div key={i} className="border border-border bg-white p-2 grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-1.5"
                 data-testid={`fe-theme-${i}`}>
          <FieldText label="Name" value={t.name} onChange={(v) => upd(i, "name", v)} />
          <FieldText label="Pillar" value={t.pillar} onChange={(v) => upd(i, "pillar", v)} />
          <FieldText label="Angle" value={t.angle} onChange={(v) => upd(i, "angle", v)} />
          <button type="button" onClick={() => remove(i)}
                      className="text-rose-600 hover:text-rose-700 pt-4"
                      data-testid={`fe-theme-del-${i}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function CadenceEditor({ value, onChange }) {
  const entries = Object.entries(value || {});
  const setChannel = (oldKey, newKey, patch) => {
    const next = {};
    for (const [k, v] of Object.entries(value || {})) {
      if (k === oldKey) next[newKey || oldKey] = { ...v, ...patch };
      else next[k] = v;
    }
    onChange(next);
  };
  const remove = (k) => {
    const next = { ...(value || {}) }; delete next[k]; onChange(next);
  };
  const [newKey, setNewKey] = useState("");
  const addChannel = () => {
    const k = newKey.trim(); if (!k) return;
    onChange({ ...(value || {}), [k]: { per_week: 3, best_days: [], best_time: "" } });
    setNewKey("");
  };
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest font-semibold">
        Cadence ({entries.length} channels)
      </div>
      <div className="space-y-1.5">
        {entries.map(([channel, c]) => (
          <div key={channel} className="border border-border bg-white p-2 grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_1.4fr_auto] gap-1.5 items-end"
                    data-testid={`fe-cadence-${channel}`}>
            <FieldText label="Channel" value={channel}
                          onChange={(v) => setChannel(channel, v, {})}
                          hint="e.g. linkedin_post" />
            <FieldNum label="Per week" min={0} value={c.per_week ?? ""}
                            onChange={(v) => setChannel(channel, channel,
                                                        { per_week: v === "" ? undefined : v,
                                                          per_month: undefined })} />
            <FieldNum label="Per month" min={0} value={c.per_month ?? ""}
                            onChange={(v) => setChannel(channel, channel,
                                                        { per_month: v === "" ? undefined : v,
                                                          per_week: undefined })} />
            <FieldText label="Best days / time"
                          value={[(c.best_days || []).join(","), c.best_time].filter(Boolean).join(" · ")}
                          onChange={(v) => {
                            const [days, time] = v.split("·").map(s => s.trim());
                            setChannel(channel, channel, {
                              best_days: days ? days.split(",").map(d => d.trim()).filter(Boolean) : [],
                              best_time: time || "",
                            });
                          }}
                          hint="e.g. Mon,Wed,Fri · 9am" />
            <button type="button" onClick={() => remove(channel)}
                        className="text-rose-600 hover:text-rose-700 pb-2"
                        data-testid={`fe-cadence-del-${channel}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-1.5 border-t border-border pt-2">
        <div className="flex-1">
          <FieldText label="Add channel" value={newKey}
                        onChange={setNewKey}
                        placeholder="e.g. x_post" />
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs"
                  onClick={addChannel} data-testid="fe-cadence-add">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function HashtagBankEditor({ value, onChange }) {
  const entries = Object.entries(value || {});
  const setPillarTags = (oldKey, newKey, tagsStr) => {
    const parsed = tagsStr.split(/[\s,]+/).map(t => t.trim()).filter(Boolean)
      .map(t => t.startsWith("#") ? t : `#${t}`);
    const next = {};
    for (const [k, v] of Object.entries(value || {})) {
      if (k === oldKey) next[newKey || oldKey] = parsed;
      else next[k] = v;
    }
    onChange(next);
  };
  const remove = (k) => {
    const next = { ...(value || {}) }; delete next[k]; onChange(next);
  };
  const [newPillar, setNewPillar] = useState("");
  const addPillar = () => {
    const k = newPillar.trim(); if (!k) return;
    onChange({ ...(value || {}), [k]: [] });
    setNewPillar("");
  };
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest font-semibold">
        Hashtag bank ({entries.length} pillars)
      </div>
      {entries.map(([pillar, tags]) => (
        <div key={pillar} className="border border-border bg-white p-2 grid grid-cols-1 md:grid-cols-[1fr_3fr_auto] gap-1.5 items-start"
                  data-testid={`fe-hashtag-${pillar}`}>
          <FieldText label="Pillar" value={pillar}
                        onChange={(v) => setPillarTags(pillar, v, (tags || []).join(" "))} />
          <FieldTextarea label="Tags (space or comma-separated)"
                              value={(tags || []).join(" ")} rows={2}
                              onChange={(v) => setPillarTags(pillar, pillar, v)}
                              placeholder="#coffeeculture #baristalife #brewathome" />
          <button type="button" onClick={() => remove(pillar)}
                      className="text-rose-600 hover:text-rose-700 pt-4"
                      data-testid={`fe-hashtag-del-${pillar}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <div className="flex items-end gap-1.5 border-t border-border pt-2">
        <div className="flex-1">
          <FieldText label="Add pillar" value={newPillar}
                        onChange={setNewPillar}
                        placeholder="e.g. Coffee culture" />
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs"
                  onClick={addPillar} data-testid="fe-hashtag-add">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function ContentMixEditor({ value, onChange }) {
  const entries = Object.entries(value || {});
  const total = entries.reduce((a, [, v]) => a + (Number(v) || 0), 0);
  const setKey = (oldKey, newKey, pct) => {
    const next = {};
    for (const [k, v] of Object.entries(value || {})) {
      if (k === oldKey) next[newKey || oldKey] = Number(pct) || 0;
      else next[k] = v;
    }
    onChange(next);
  };
  const remove = (k) => {
    const next = { ...(value || {}) }; delete next[k]; onChange(next);
  };
  const [newKey, setNewKey] = useState("");
  const addCat = () => {
    const k = newKey.trim(); if (!k) return;
    onChange({ ...(value || {}), [k]: 0 });
    setNewKey("");
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold">
          Content mix ({entries.length}) · total {total}%
        </div>
        {total !== 100 && entries.length > 0 && (
          <span className="text-[10px] text-amber-700 font-semibold">⚠︎ Should sum to 100</span>
        )}
      </div>
      {entries.map(([k, v]) => (
        <div key={k} className="border border-border bg-white p-2 grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-1.5 items-end"
                  data-testid={`fe-mix-${k}`}>
          <FieldText label="Category" value={k}
                        onChange={(nv) => setKey(k, nv, v)} />
          <FieldNum label="%" min={0} max={100} value={v}
                          onChange={(nv) => setKey(k, k, nv)} />
          <button type="button" onClick={() => remove(k)}
                      className="text-rose-600 hover:text-rose-700 pb-2"
                      data-testid={`fe-mix-del-${k}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <div className="flex items-end gap-1.5 border-t border-border pt-2">
        <div className="flex-1">
          <FieldText label="Add category" value={newKey}
                        onChange={setNewKey}
                        placeholder="e.g. educational" />
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs"
                  onClick={addCat} data-testid="fe-mix-add">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function KeyDatesEditor({ value, onChange }) {
  const dates = value || [];
  const upd = (i, k, v) => {
    const next = [...dates]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };
  const remove = (i) => onChange(dates.filter((_, j) => j !== i));
  const add = () => onChange([...dates, { date_hint: "", angle: "" }]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold">
          Dates to watch ({dates.length})
        </div>
        <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                  onClick={add} data-testid="fe-date-add">
          <Plus className="w-3 h-3 mr-1" /> Add date
        </Button>
      </div>
      {dates.map((d, i) => (
        <div key={i} className="border border-border bg-white p-2 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-1.5 items-end"
                 data-testid={`fe-date-${i}`}>
          <FieldText label="When" value={d.date_hint}
                        onChange={(v) => upd(i, "date_hint", v)}
                        placeholder="e.g. March (Women's Day)" />
          <FieldTextarea label="Angle" value={d.angle} rows={2}
                              onChange={(v) => upd(i, "angle", v)}
                              placeholder="Content angle for this moment" />
          <button type="button" onClick={() => remove(i)}
                      className="text-rose-600 hover:text-rose-700 pb-2"
                      data-testid={`fe-date-del-${i}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ---------- Generic quick-edit dialog (friendly + advanced JSON) ---------- */
export function QuickEditDialog({
  open, onOpenChange, wsId, bid, sid, section, current, onSaved,
}) {
  const [busy, setBusy] = useState(false);
  const [value, setValue] = useState(null);
  const [note, setNote] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const isText = section === "summary" || section === "north_star_metric";
  const isList = section === "recommendations";
  const hasFriendly = !isText && !isList;

  useEffect(() => {
    if (!open) return;
    // Reset draft state each time the dialog opens
    setNote("");
    setAdvanced(false);
    if (isText) {
      setValue(current ?? "");
    } else if (isList) {
      setValue([...(current || [])]);
    } else {
      setValue(deepClone(current ?? (isArraySection(section) ? [] : {})));
    }
  }, [open, section, current, isText, isList]);

  useEffect(() => {
    if (advanced) {
      try { setJsonText(JSON.stringify(value, null, 2)); }
      catch { setJsonText(""); }
    }
  }, [advanced, value]);

  const save = async () => {
    let payload = value;
    if (advanced) {
      try { payload = JSON.parse(jsonText); }
      catch { toast.error("Invalid JSON — please fix and retry"); return; }
    }
    setBusy(true);
    try {
      const r = await api.patch(
        `/workspaces/${wsId}/brands/${bid}/content-strategy/${sid}`,
        { [section]: payload, edit_note: note || undefined });
      toast.success(`${SECTION_LABELS[section]} saved`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                          data-testid={`edit-section-dialog-${section}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenSquare className="w-4 h-4 text-primary" />
            Edit · {SECTION_LABELS[section] || section}
            {hasFriendly && (
              <button type="button"
                          onClick={() => setAdvanced(a => !a)}
                          className="ml-auto text-[10px] text-muted-foreground hover:text-primary underline underline-offset-2"
                          data-testid="edit-section-advanced-toggle">
                {advanced ? "← Back to friendly" : "Advanced (JSON) →"}
              </button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          {isText && (
            <Textarea rows={section === "summary" ? 6 : 2}
                        value={value ?? ""} onChange={(e) => setValue(e.target.value)}
                        className="rounded-none font-sans"
                        placeholder={section === "summary"
                          ? "Executive summary for the client…"
                          : "e.g. MQL, Book demos, Email signups"}
                        data-testid="edit-section-text" />
          )}
          {isList && (
            <>
              <div className="text-[10px] uppercase tracking-widest font-semibold">
                Recommendations ({(value || []).length})
              </div>
              {(value || []).map((r, i) => (
                <div key={i} className="flex items-start gap-1.5"
                          data-testid={`fe-rec-${i}`}>
                  <span className="text-primary font-bold pt-1.5">→</span>
                  <Textarea rows={2} className="rounded-none text-xs flex-1"
                                value={r}
                                onChange={(e) => {
                                  const next = [...(value || [])];
                                  next[i] = e.target.value;
                                  setValue(next);
                                }} />
                  <button type="button"
                              onClick={() => setValue((value || []).filter((_, j) => j !== i))}
                              className="text-rose-600 hover:text-rose-700 pt-1.5"
                              data-testid={`fe-rec-del-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                        onClick={() => setValue([...(value || []), ""])}
                        data-testid="fe-rec-add">
                <Plus className="w-3 h-3 mr-1" /> Add recommendation
              </Button>
            </>
          )}
          {hasFriendly && !advanced && (
            <FriendlySectionEditor section={section} value={value} onChange={setValue} />
          )}
          {hasFriendly && advanced && (
            <>
              <Textarea rows={16} value={jsonText}
                          onChange={(e) => setJsonText(e.target.value)}
                          className="rounded-none font-mono text-[11px] leading-tight"
                          data-testid="edit-section-json" />
              <div className="text-[10px] text-muted-foreground">
                JSON structure — preserve keys. Invalid JSON will be rejected.
              </div>
            </>
          )}
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Reason for edit (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                     className="rounded-none h-8"
                     placeholder="e.g. Client asked to skew MOFU"
                     data-testid="edit-section-note" />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="ghost" className="rounded-none"
                    onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy}
                    data-testid="edit-section-save-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenSquare className="w-4 h-4 mr-2" />}
            {busy ? "Saving…" : "Save manual edit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FriendlySectionEditor({ section, value, onChange }) {
  switch (section) {
    case "content_pillars": return <PillarsEditor value={value} onChange={onChange} />;
    case "proposed_topics": return <TopicsEditor value={value} onChange={onChange} />;
    case "themes": return <ThemesEditor value={value} onChange={onChange} />;
    case "cadence": return <CadenceEditor value={value} onChange={onChange} />;
    case "hashtag_bank": return <HashtagBankEditor value={value} onChange={onChange} />;
    case "content_mix": return <ContentMixEditor value={value} onChange={onChange} />;
    case "key_dates_to_watch": return <KeyDatesEditor value={value} onChange={onChange} />;
    default:
      return (
        <div className="text-xs text-muted-foreground italic">
          No friendly editor for this section — use Advanced (JSON).
        </div>
      );
  }
}

function isArraySection(section) {
  return ["content_pillars", "proposed_topics", "themes", "recommendations",
          "key_dates_to_watch"].includes(section);
}
function deepClone(v) {
  try { return JSON.parse(JSON.stringify(v)); }
  catch { return v; }
}

/* ---------- Revision history dialog ---------- */
export function RevisionHistoryDialog({ open, onOpenChange, wsId, bid, sid }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [counts, setCounts] = useState({ ai: 0, manual: 0, carry_over: 0 });
  const [version, setVersion] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!open || !sid) return;
    setLoading(true);
    api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/${sid}/revisions`)
      .then(r => {
        setRows(r.data.rows || []);
        setMeta(r.data.section_meta || {});
        setCounts(r.data.counts || {});
        setVersion(r.data.version || null);
      })
      .catch(e => toast.error(apiErrorMessage(e, "Failed to load revisions")))
      .finally(() => setLoading(false));
  }, [open, sid, wsId, bid]);

  const filtered = rows.filter(r => {
    if (filter === "all") return true;
    if (filter === "manual") return r.source === "manual" || r.kind === "manual_edit";
    if (filter === "ai") return r.source === "ai" || r.kind === "ai_generate";
    if (filter === "carry_over") return r.source === "carry_over" || r.kind === "carry_over";
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
                     data-testid="revision-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-primary" />
            Revision history · v{version}
          </DialogTitle>
        </DialogHeader>

        {/* KPI chips + filter */}
        <div className="flex items-center gap-2 flex-wrap border-b border-border pb-2">
          <ChipFilter label={`All · ${rows.length}`} active={filter === "all"} onClick={() => setFilter("all")} />
          <ChipFilter icon={Bot} label={`AI · ${counts.ai || 0}`}
                        active={filter === "ai"} tone="sky"
                        onClick={() => setFilter("ai")} />
          <ChipFilter icon={PenSquare} label={`Manual · ${counts.manual || 0}`}
                        active={filter === "manual"} tone="amber"
                        onClick={() => setFilter("manual")} />
          <ChipFilter icon={ArrowLeftRight} label={`Carried · ${counts.carry_over || 0}`}
                        active={filter === "carry_over"} tone="violet"
                        onClick={() => setFilter("carry_over")} />
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {loading && (
            <div className="text-sm text-muted-foreground text-center p-8">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-sm text-muted-foreground text-center p-8" data-testid="revision-history-empty">
              No revisions match this filter.
            </div>
          )}
          {filtered.map((r, i) => (
            <RevisionRow key={r.id || i} rev={r} expanded={expanded === (r.id || i)}
                            onToggle={() => setExpanded(expanded === (r.id || i) ? null : (r.id || i))} />
          ))}
        </div>

        {/* Section source panel */}
        <div className="border-t border-border pt-2 mt-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
            Current section sources
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
            {TRACKED_SECTIONS.map(s => (
              <div key={s} className="flex items-center gap-1.5 text-[11px] border border-border/50 bg-muted/20 px-1.5 py-1">
                <SectionSourceBadge meta={meta[s]} small />
                <span className="truncate">{SECTION_LABELS[s]}</span>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="ghost" className="rounded-none" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChipFilter({ icon: Icon, label, active, tone = "primary", onClick }) {
  const toneCls = {
    primary: active ? "border-primary bg-primary text-primary-foreground" : "border-border",
    sky: active ? "border-sky-500 bg-sky-500 text-white" : "border-border",
    amber: active ? "border-amber-500 bg-amber-500 text-white" : "border-border",
    violet: active ? "border-violet-500 bg-violet-500 text-white" : "border-border",
  }[tone];
  return (
    <button type="button" onClick={onClick}
              className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-semibold ${toneCls}`}
              data-testid={`revision-filter-${label.split(" ")[0].toLowerCase()}`}>
      {Icon && <Icon className="w-3 h-3" />} {label}
    </button>
  );
}

function RevisionRow({ rev, expanded, onToggle }) {
  const kind = rev.kind || rev.source || "ai";
  const isAI = kind === "ai_generate" || rev.source === "ai";
  const isCarry = kind === "carry_over" || rev.source === "carry_over";
  const Icon = isAI ? Bot : isCarry ? ArrowLeftRight : PenSquare;
  const tone = isAI ? "border-l-sky-500 bg-sky-50/40"
                    : isCarry ? "border-l-violet-500 bg-violet-50/40"
                              : "border-l-amber-500 bg-amber-50/40";
  const section = rev.section === "*all*" ? "All sections" : (SECTION_LABELS[rev.section] || rev.section);
  const showDiff = !isAI && (rev.before !== undefined || rev.after !== undefined);

  return (
    <div className={`border border-border border-l-4 ${tone}`} data-testid={`revision-row-${rev.id || ""}`}>
      <button type="button" onClick={onToggle}
                className="w-full flex items-center gap-2 p-2 text-left hover:bg-muted/20">
        <Icon className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">
            {isAI ? "Generated by AI" : isCarry ? "Carried over" : "Manually edited"} · {section}
          </div>
          <div className="text-[10px] text-muted-foreground truncate">
            {rev.actor_name || "system"} · {fmtRelative(rev.at)}
            {rev.carried_from_version && ` · from v${rev.carried_from_version}`}
            {rev.note && ` · "${rev.note}"`}
          </div>
        </div>
        <ArrowRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && showDiff && (
        <div className="border-t border-border/60 p-2 grid grid-cols-1 md:grid-cols-2 gap-2 bg-white">
          <DiffPanel label="Before" value={rev.before} tone="rose" />
          <DiffPanel label={isCarry ? "Carried in" : "After"} value={rev.after} tone="emerald" />
        </div>
      )}
    </div>
  );
}

function DiffPanel({ label, value, tone }) {
  const toneCls = tone === "rose" ? "border-rose-300 bg-rose-50" : "border-emerald-300 bg-emerald-50";
  const txt = value === undefined || value === null
    ? "(empty)"
    : typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <div className={`border ${toneCls} p-2 max-h-52 overflow-auto`}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">{label}</div>
      <pre className="text-[10px] font-mono whitespace-pre-wrap leading-tight">{txt}</pre>
    </div>
  );
}

/* ---------- Carry-over dialog ---------- */
export function CarryOverDialog({
  open, onOpenChange, wsId, bid, currentStrategy, versions, onDone,
}) {
  const [fromSid, setFromSid] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [srcStrategy, setSrcStrategy] = useState(null);

  const otherVersions = (versions || []).filter(v => v.id !== currentStrategy?.id);

  useEffect(() => {
    if (!open) {
      setFromSid(""); setSelected({}); setSrcStrategy(null); setNote("");
    }
  }, [open]);

  useEffect(() => {
    if (!fromSid) { setSrcStrategy(null); return; }
    api.get(`/workspaces/${wsId}/brands/${bid}/content-strategy/${fromSid}`)
      .then(r => setSrcStrategy(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Failed to load source")));
  }, [fromSid, wsId, bid]);

  // Sections that have a distinct manual edit in the source vs current
  const candidateSections = useMemo(() => {
    if (!srcStrategy || !currentStrategy) return [];
    return TRACKED_SECTIONS.filter(s => {
      const srcMeta = (srcStrategy.section_meta || {})[s];
      const isManual = srcMeta?.source === "manual" || srcMeta?.source === "carry_over";
      const srcVal = JSON.stringify(srcStrategy[s] ?? null);
      const dstVal = JSON.stringify(currentStrategy[s] ?? null);
      // show if it's a manual section in source AND differs from destination
      return isManual && srcVal !== dstVal;
    });
  }, [srcStrategy, currentStrategy]);

  const toggle = (s) => setSelected(prev => ({ ...prev, [s]: !prev[s] }));
  const toggleAll = () => {
    if (candidateSections.every(s => selected[s])) setSelected({});
    else setSelected(Object.fromEntries(candidateSections.map(s => [s, true])));
  };

  const apply = async () => {
    const secs = Object.keys(selected).filter(k => selected[k]);
    if (!secs.length) { toast.error("Pick at least one section to carry over"); return; }
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brands/${bid}/content-strategy/${currentStrategy.id}/carry-over`,
        { from_sid: fromSid, sections: secs, note: note || undefined });
      toast.success(`Carried over ${secs.length} section${secs.length > 1 ? "s" : ""}`);
      onDone?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Carry-over failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="carry-over-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-4 h-4 text-primary" />
            Carry over manual edits into v{currentStrategy?.version}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Source version</Label>
            <select className="w-full h-9 border border-border rounded-none px-2 text-sm bg-background"
                       value={fromSid} onChange={(e) => setFromSid(e.target.value)}
                       data-testid="carry-over-source">
              <option value="">— pick a version —</option>
              {otherVersions.map(v => (
                <option key={v.id} value={v.id}>
                  v{v.version} · {v.status} · {v.created_by_name || "—"}
                </option>
              ))}
            </select>
          </div>

          {fromSid && !srcStrategy && (
            <div className="text-sm text-muted-foreground p-4 text-center">Loading source…</div>
          )}

          {srcStrategy && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="label-eyebrow">Sections with manual edits in source ({candidateSections.length})</Label>
                {candidateSections.length > 0 && (
                  <button type="button" onClick={toggleAll}
                            className="text-[10px] underline text-primary"
                            data-testid="carry-over-toggle-all">
                    {candidateSections.every(s => selected[s]) ? "Clear" : "Select all"}
                  </button>
                )}
              </div>
              {candidateSections.length === 0 ? (
                <div className="text-sm text-muted-foreground border border-dashed border-border p-4 text-center">
                  No manual edits differ between source and current. Nothing to carry over.
                </div>
              ) : (
                <div className="border border-border max-h-72 overflow-y-auto divide-y divide-border">
                  {candidateSections.map(s => {
                    const srcMeta = (srcStrategy.section_meta || {})[s];
                    return (
                      <label key={s}
                                className={`flex items-start gap-2 p-2 cursor-pointer hover:bg-muted/20 ${selected[s] ? "bg-amber-50/40" : ""}`}
                                data-testid={`carry-over-section-${s}`}>
                        <Checkbox checked={!!selected[s]} onCheckedChange={() => toggle(s)}
                                     className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-semibold">{SECTION_LABELS[s]}</span>
                            <SectionSourceBadge meta={srcMeta} small />
                          </div>
                          <div className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">
                            {previewValue(srcStrategy[s])}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {srcStrategy && candidateSections.length > 0 && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="e.g. Client-supplied summary — must preserve"
                       className="h-8 rounded-none"
                       data-testid="carry-over-note" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={apply}
                    disabled={busy || !fromSid || candidateSections.length === 0
                                 || !Object.values(selected).some(Boolean)}
                    data-testid="carry-over-apply-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitMerge className="w-4 h-4 mr-2" />}
            Carry {Object.values(selected).filter(Boolean).length || ""} section(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function previewValue(v) {
  if (v === null || v === undefined) return "(empty)";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return `${v.length} items · ${v.slice(0, 3).map(x => typeof x === "string" ? x : (x?.name || x?.title || "…")).join(" · ")}`;
  if (typeof v === "object") return `${Object.keys(v).length} entries`;
  return String(v);
}

/* ---------- Edit-policy pill ---------- */
export function EditPolicyPill({ policy, onChange, canManage }) {
  const [busy, setBusy] = useState(false);
  const label = policy === "brand_lead_admin" ? "Lead / Admin only" : "Any member";
  const next = policy === "brand_lead_admin" ? "any_member" : "brand_lead_admin";
  return (
    <button type="button"
              onClick={async () => {
                if (!canManage || busy) return;
                setBusy(true);
                try { await onChange(next); } finally { setBusy(false); }
              }}
              disabled={!canManage || busy}
              title={canManage ? "Toggle who can manually edit this strategy" : "Only managers can change this"}
              className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest
                          ${policy === "brand_lead_admin"
                              ? "border-rose-500 text-rose-800 bg-rose-50"
                              : "border-emerald-500 text-emerald-800 bg-emerald-50"}
                          ${!canManage ? "opacity-70 cursor-not-allowed" : "hover:opacity-80"}`}
              data-testid="content-strategy-edit-policy-pill">
      <Shield className="w-3 h-3" /> {label}
    </button>
  );
}
